import { get, post } from '../../helpers/api';

// HITCH-5175 exposes trip-throttle pauses as a plain REST resource (not the
// admin GraphQL API) — cursor-paginated, unlike the offset-based listings
// elsewhere in this file's siblings. Served under the admin app's global
// prefix, so the path is /admin/* like every other admin route.
const BASE = '/admin/trip-throttles';

const DEFAULT_LIMIT = 25;
const MAX_LIMIT = 100;

// Surface the server's error message (e.g. duplicate city+day, past date,
// unknown city) instead of axios's generic "Request failed with status code ...".
const messageFrom = (e) => e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Request failed';

const getTripThrottles = async ({ cityId, status, fromDate, toDate, cursor, limit } = {}) => {
  const params = new URLSearchParams();
  if (cityId) params.set('cityId', cityId);
  if (status) params.set('status', status);
  if (fromDate) params.set('fromDate', fromDate);
  if (toDate) params.set('toDate', toDate);
  if (cursor) params.set('cursor', cursor);
  // Clamp client-side to the server's max rather than trusting callers not to
  // pass an unbounded limit.
  params.set('limit', String(Math.min(limit ?? DEFAULT_LIMIT, MAX_LIMIT)));

  const { data } = await get(`${BASE}?${params.toString()}`);
  return { entries: data?.entries ?? [], nextCursor: data?.nextCursor ?? null };
};

const createTripThrottle = async ({
  cityId,
  serviceDate,
  direction,
  productScope,
  reason,
  reasonNote,
  // Intra-day departure window (HITCH-5254). ORIGIN-only; null/omitted = all-day.
  // Must be forwarded explicitly — buildCreateInput emits these, and the backend
  // create DTO accepts + persists them, but they were being dropped here.
  windowStart,
  windowEnd,
}) => {
  try {
    const { data } = await post(BASE, {
      cityId,
      serviceDate,
      direction,
      productScope,
      reason,
      reasonNote,
      windowStart,
      windowEnd,
    });
    return data;
  } catch (e) {
    // Rethrown as a plain Error so 409 (duplicate city+day) / 400 (past date)
    // / 404 (unknown city) messages are catchable and renderable inline by
    // the create form, instead of a raw axios error object.
    throw new Error(messageFrom(e));
  }
};

const cancelTripThrottle = async ({ id }) => {
  try {
    const { data } = await post(`${BASE}/${id}/cancel`);
    return data;
  } catch (e) {
    throw new Error(messageFrom(e));
  }
};

export default {
  getTripThrottles,
  createTripThrottle,
  cancelTripThrottle,
};
